import moment from "moment";
import { Masks } from "@/utils";
import i18n from "@/i18n";

export function formatPercentage(value:any, decimals = 2) {
    return `${Number(value).toFixed(decimals)} %`;
}

function padTo2Digits(num: number) {
    return num.toString().padStart(2, '0');
}

export function formatDateLocale(date: string, inMask: string, inLocale: string, outMask = Masks.dateMask) {
    return moment(date, inMask, inLocale).format(outMask);
}

export function formatDateTimeLocale(date: string, inMask: string, inLocale: string, outMask = Masks.dateMask) {
    return moment(date, inMask, inLocale).format(outMask);
}

/**
 * Format a date value, considering internationalization and different date formats.
 *
 * This function evaluates the input date value and uses appropriate formatting based on a provided mask and locale.
 *
 * @param {any} value - The date value to format.
 * @return {string} The formatted date as a string.
 */
export function formatDateFromUntil(value: any) {
    value = value.includes("/")
        ? formatDateLocale(
            value,
            i18n.global.t("mask.format"),
            i18n.global.locale.value,
            i18n.global.t("mask.format")
        )
        : formatDateLocale(
            value,
            i18n.global.t("mask.formatWo"),
            i18n.global.locale.value,
            i18n.global.t("mask.format")
        );
}
/**
 * Format a date value for display in an international context.
 *
 * This function takes a date value and formats it for display using the configured internationalization settings.
 *
 * @param {any} date - The date value to format for display.
 * @return {string} The formatted date for display as a string.
 */
export function formatDateGlobal(date: any) {
    return formatDateLocale(
        date,
        i18n.global.t("mask.format"),
        i18n.global.locale.value,
        i18n.global.t("mask.format_en")
    );
}

export function formatDate(param: Date | string, separator: string = '/') {
    let date = typeof param === "string" ? new Date(param) : param
    return (
        [
            padTo2Digits(date.getDate()),
            padTo2Digits(date.getMonth() + 1),
            date.getFullYear(),
        ].join(separator)
    );
}

export function formatedDate(param: Date | string, separator: string = '/'): string {
    let date = typeof param === "string" ? new Date(param + 'T00:00:00Z') : param; 

    let year = date.getUTCFullYear();
    let month = date.getUTCMonth() + 1;
    let day = date.getUTCDate();

    if (month === 12 && day >= 27 && day <= 31 && hasDoubleDigitEnding(year)) {
        return formatYearDate(day, month, year, separator);
    }
    if (month === 12) {
        let lastSunday = getLastSundayOfYear(new Date(year, 11, 31));

        if (date > lastSunday && date < new Date(Date.UTC(year + 1, 0, 1))) {
            year -= 1;
        }
    }

    return formatYearDate(day, month, year, separator);
}

// Fonction qui détecte si l'année se termine par deux chiffres identiques
function hasDoubleDigitEnding(year: number): boolean {
    const yearString = String(year);
    return yearString[yearString.length - 2] === yearString[yearString.length - 1];
}

function formatYearDate(day: number, month: number, year: number, separator: string): string {
    return [padTo2Digits(day), padTo2Digits(month), year].join(separator);
}

function getLastSundayOfYear(date: Date): Date {
    let lastDay = date.getDate();
    let dayOfWeek = date.getUTCDay();

    let daysToSubtract = dayOfWeek; 

    return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), lastDay - daysToSubtract));
}

export function formatTimestamp(param: Date | string, separator: string = '/') {
    const date = typeof param === "string" ? new Date(param) : param
    return (
        [
            padTo2Digits(date.getDate()),
            padTo2Digits(date.getMonth() + 1),
            date.getFullYear(),
        ].join(separator) +
        ' ' +
        [
            padTo2Digits(date.getHours()),
            padTo2Digits(date.getMinutes()),
            padTo2Digits(date.getSeconds()),
        ].join(':')
    );
}


export function formatDateService(param: Date | string, separator: string = '/') {
    const date = typeof param === "string" ? new Date(param) : param
    return (
        [
            date.getFullYear(),
            padTo2Digits(date.getMonth() + 1),
            padTo2Digits(date.getDate()),
        ].join(separator)
    );
}

export function formatDateServiceAddDays(numberOfDays: number, param: Date | string, separator: string = '/') {
    const date = typeof param === "string" ? addDays(new Date(param), numberOfDays) : addDays(param, numberOfDays)
    return (
        [
            date.getFullYear(),
            padTo2Digits(date.getMonth() + 1),
            padTo2Digits(date.getDate()),
        ].join(separator)
    );
}

export function addDays(date: Date, days: number): Date {
    date.setDate(date.getDate() + days);
    return date;
}

export function isValidDate(dateStr: string, inMask: string, inLocale: string) {
    return dateStr.length == 10 && moment(dateStr, inMask, inLocale).isValid();
}

export function getYearsOfDate(dateStr: string) {
    return moment().diff(dateStr, 'years');
}
export function getLikeValue(value: any) {
    return `'%${value}%'`
}
export function getLikeValueStart(value: any) {
    return `'%${value}'`
}

export function getEqualValue(value: any) {
    return `'${value}'`
}

export function getEqualValueStringOrBoolean(value: any) {
    return typeof value === 'boolean' ? value : `'${value}'`
}

export function getInValue(value: any) {
    var inString: string = ''
    var firstString: string = value[0]
    if (value !== null && value.length > 0) {
        inString = `'${value[0]}'`
        value.filter((id: any) => { return id !== firstString; })
            .forEach((id: string) => inString = inString.concat(`,'${id}'`))
    }
    return inString
}

export function getStringDate(value: String) {
    return `'%${value}.%'`
}

export function getIbanMask(locale: string = 'FRA') {
    switch (locale) {
        case 'FRA':
            return 'FR ## #### #### #### #### XXXX X##'
        case 'MAR':
            return 'MA ## ## ### ############# ##'
        case 'TUN':
            return 'TN ## ## ### ############# ##'
    }
}


export function getBicMask(locale: string = 'FRA') {
    switch (locale) {
        case 'FRA':
            return 'NNNNNNNNXXX'

        default:
            return
    }


}

/**
 * Retrieves the siren mask based on the provided locale.
 *
 * @param locale - Optional parameter representing the locale.
 * @returns The siren mask corresponding to the locale.
 */
export function getSirenMask(locale?: string) {
    switch (locale) {
        case "party-FrenchOrganization":
            return '#########'

        default:
            return
    }
}


export function calculatePercentByValue(value: number, percent?: number): number {
    return percent && percent > 0 ? (value * percent) / 100 : 0;
}
export function formatString(value: string) {
    return value.replace(".", ",")
}
export function formatMask(locale: string, value: number, minDecimal: number) {
    return new Intl.NumberFormat(locale, { minimumFractionDigits: minDecimal }).format(value)
}
export function formatWithMask(value: number, minDecimal: number) {
    return formatMask(i18n.global.t('locale'), value, minDecimal)
}
/**
 * Formats a given amount as a currency using the specified currency and locale.
 * @param {string} currency - The currency code to use for formatting (e.g., "USD", "EUR").
 * @param {number} value - The value to be formatted as currency.
 * @param {string} locale - The locale to use for formatting (e.g., "en-US", "fr-FR").
 * @returns {string} The formatted currency string.
 */
export function formatAmoutswitchCurrency(currency: string, value: number) {
    if (value == null || isNaN(value)) {
      return `  -  `;
    }
    return new Intl.NumberFormat(i18n.global.locale.value, {
      style: 'currency',
      currency
    }).format(value);
  }
  
//TODO developer other formats
export function deleteFormatNumber(value: string, locale: string) {
    if (locale === 'fr') {
        return value.trim().replace(/\s/g, '').replace(",", ".")
    } else return value
}

export function deleteFormatMaskAndConvertToNumber(value: string) {
    return parseFloat(deleteFormatNumber(value, i18n.global.t('locale')))
}

export function formatDateToMoment(param: Date | string, separator: string = '/') {
    const dateMomentObject = moment(param, "DD/MM/YYYY");
    const date = dateMomentObject.toDate();
    return (
        [
            date.getFullYear(),
            padTo2Digits(date.getMonth() + 1),
            padTo2Digits(date.getDate()),
        ].join(separator)
    );
}


export function keyHandler(evt: any) {
    if (Number(evt.srcElement.value) <= 0 && evt.key === "ArrowDown") {
        evt.preventDefault();
    }
    if (
        evt.key === "-" ||
        evt.key === "." ||
        evt.key === "," ||
        evt.key === "e" ||
        evt.key === "E"
    ) {
        evt.preventDefault();
    }
}

/**
  * Calculates the total of column values for the selected rows
  * @param selectRows - The selected rows
  * @param colName - The column name for which to calculate the total
  * @returns The total of the column values
  */
export function calculTotal(selectRows: any, colName: string) {

    const total = selectRows
        .map((row: any) => row[colName])
        .reduce((acc: number, val: number) => acc + val, 0);
    return isNaN(total) ? "" : total;
}

export function passwordValidator(password: string): string {
    return Masks.passwordMask.test(password) || password === '' ? '' : i18n.global.t("main.connect.rulesPassowrd");
}

export function newPasswordController(userNewPassword: string): string {
    return passwordValidator(userNewPassword);
}

export function confirmPasswordController(confirmPassword: string, userNewPassword: string): string {
    return confirmPassword === userNewPassword || confirmPassword === '' ? '' : i18n.global.t("main.connect.changePasswordMessageFailed");
}

export function required(val: unknown) {
    return !!val || i18n.global.t("main.register.required");
}

export function formatDateToCompleteDate(date: string, mask: string) {
    const payload = moment(date);
    return payload.format(mask);
}

export function checkFormatDate (fieldValue: string) : boolean{
    // Check the format is dd/mm/yyyy or mm/dd/yyyy
    const regex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
    return regex.test(fieldValue) ? true : false; 
}
export function getGraceTimePeriod (period: any) {
    if (period.unit === 'DAYS'){
        return 'DAYS'
    }
    if (period.unit === 'MONTHS') {
        if (period.duration % 12 === 0) {
            return 'years';
        } else if (period.duration % 6 === 0) {
             return 'semester';
        } else if (period.duration % 3 === 0) {
            return 'quarter';
    }  
    return 'MONTHS';
    }
}

export function getLikeStartValue(value: any) {
    return `'${value}%'`
}
export function addDaysToStringDate(date: string | undefined, days: number): string | undefined {
  if (!date) {
    return undefined;
  }
  else {
    const parts = date.split("/");
    let day = Number(parts[0]);
    const month = Number(parts[1]) - 1; // Months start from 0
    const year = Number(parts[2]);

    const newdate = new Date(year, month, day);

    newdate.setDate(newdate.getDate() + days);

    const newDay = String(newdate.getDate()).padStart(2, "0");
    const newMonth = String(newdate.getMonth() + 1).padStart(2, "0");
    const newYear = newdate.getFullYear();

    return `${newDay}/${newMonth}/${newYear}`;
  }
}

export function formatRelationValidityDate(value?: string | null): string {
  if (!value || value === "Invalid date") return "";

  let formatted: string;
  if (value.includes("/")) {
    formatted = formatDateLocale(value, "DD/MM/YYYY", "fr");
  } else if (value.includes("-")) {
    formatted = formatDateLocale(value, "YYYY-MM-DD", "fr");
  } else {
    formatted = formatDateLocale(value, "DDMMYYYY", "fr");
  }

  return formatted === "Invalid date" ? "" : formatted;
}

export function normalizeRelationValidity(validity?: {
  from?: string;
  until?: string | null;
}) {
  if (!validity) return;
  if(validity?.from?.includes("-") && validity.until?.includes("-") ) return;

  const formattedFrom = formatRelationValidityDate(validity.from);
  validity.from = formattedFrom || moment().format(Masks.dateMask);

  validity.until = formatRelationValidityDate(validity.until);
}

export function parsePartyRoles(
  roles?: string | string[] | null
): string[] {
  if (!roles) return [];
  if (Array.isArray(roles)) return roles.filter(Boolean);
  if (typeof roles !== "string") return [];

  try {
    const parsed = JSON.parse(roles);
    return Array.isArray(parsed) ? parsed.filter(Boolean) : [roles];
  } catch {
    return roles ? [roles] : [];
  }
}

export function formatPartyRoleLabels(
  roles?: string | string[] | null,
  separator = ", "
): string {
  return parsePartyRoles(roles)
    .map((role) =>
      i18n.global.t("demand.party.roles.".concat(role)).toUpperCase()
    )
    .join(separator);
}